import forwardRefAs from 'forward-ref-as';
import React, { FC } from 'react';
import { IconProps } from '../../assets/images/svg';
import { cn } from '../../helpers';
import styles from './button.module.scss';

export interface ButtonProps {
    /**
     * Icon prefixed to the text
     */
    startIcon?: React.ReactElement<IconProps>;

    /**
     * Icon postfixed to the text
     */
    endIcon?: React.ReactElement<IconProps>;

    /**
     * Button styling variants
     */
    variant?: 'solid' | 'outline' | 'ghost';

    /**
     * Give the button rounded corners otherwise it is a square
     */
    isRounded?: boolean;

    /**
     * Button sizes
     */
    size?: 'small' | 'medium' | 'large';
}

export const Button = forwardRefAs<'button', ButtonProps>(
    (
        {
            as: As = 'button',
            children,
            startIcon,
            endIcon,
            variant = 'solid',
            size = 'medium',
            isRounded,
            className,
            ...props
        },
        ref
    ) => {
        const styleClasses = cn(styles.button, {
            [className]: className,
            [styles.small]: size === 'small',
            [styles.medium]: size === 'medium',
            [styles.large]: size === 'large',
            [styles.rounded]: isRounded,
            [styles.outline]: variant === 'outline',
            [styles.solid]: variant === 'solid',
            [styles.ghost]: variant === 'ghost',
        });

        return (
            <As className={styleClasses} ref={ref} {...props}>
                {startIcon && <ButtonIcon> {startIcon}</ButtonIcon>}
                {children}
                {endIcon && <ButtonIcon> {endIcon}</ButtonIcon>}
            </As>
        );
    }
);

Button.displayName = 'Button';

const ButtonIcon: FC = ({ children, ...props }) => {
    const validChildren = React.isValidElement(children)
        ? React.cloneElement(children, {
              'aria-hidden': true,
          })
        : children;
    return (
        <span className={styles.buttonIcon} {...props}>
            {validChildren}
        </span>
    );
};
